import { createSlice, createAsyncThunk, isAnyOf } from '@reduxjs/toolkit';
import { ShipmentService } from '../../../service/api/shipment-service';
import { showToast } from 'Core/redux/toast/slice';
import { closeModal, showModal } from 'Core/redux/main/slice';
import { redux } from 'Core/redux';
import { saveAs } from 'Core/utils';
import { MODAL_TYPES } from 'Feature/components/app-modals';

const { SHIPMENT_ADD_EDIT } = MODAL_TYPES;

export const DEFAULT_SHIPMENT_PAGE_SIZE = 50;

export const STORE_KEY = 'shipment';
export const INITIAL_STATE = {
  data: [],
  loading: false,
  loadingMore: false,
  error: null,
  /** Latest `fetchShipments` thunk requestId — ignore stale fulfilled/rejected (e.g. slow unfiltered load after filtered search). */
  shipmentListRequestId: null,
  shipmentLoadMoreRequestId: null,
  /** Bumped on each non-append list fetch so in-flight load-more responses are ignored. */
  listGeneration: 0,
  pagination: {
    pageNumber: 1,
    pageSize: DEFAULT_SHIPMENT_PAGE_SIZE,
    totalCount: 0,
    hasMore: true,
  },
  filters: {
    /** Header search — sent as partNumber/poNumber (ShippingItem lines). */
    searchText: '',
    /** Toolbar search — sent as searchText/@Search (ASP, trailer, PSN, customer). */
    pageSearchText: '',
    customer: null,
    status: null,
    shipDate: null,
    partNumber: '',
  },
  filterVisible: false,
  tileView: true,
  selectedShipment: null,
  editModalVisible: false,
};

const normalizePagedResponse = payload => {
  if (Array.isArray(payload)) {
    return {
      items: payload,
      totalCount: payload.length,
    };
  }

  const items = payload?.items ?? payload?.Items ?? [];
  const totalCount = payload?.totalCount ?? payload?.TotalCount ?? items.length;
  return {
    items: Array.isArray(items) ? items : [],
    totalCount: typeof totalCount === 'number' ? totalCount : 0,
  };
};

export const fetchShipments = createAsyncThunk(
  'shipment/fetchShipments',
  async (arg, { rejectWithValue, getState }) => {
    try {
      const { append = false, pageNumber, pageSize, ...filters } = arg || {};
      const state = getState().shipment;
      const listGeneration = state.listGeneration;
      const resolvedPageNumber =
        pageNumber ?? (append ? state.pagination.pageNumber + 1 : 1);
      const resolvedPageSize = pageSize ?? state.pagination.pageSize ?? DEFAULT_SHIPMENT_PAGE_SIZE;

      const response = await ShipmentService.getAllShipmentsPaged({
        ...filters,
        pageNumber: resolvedPageNumber,
        pageSize: resolvedPageSize,
      });

      if (response.status === 200) {
        const { items, totalCount } = normalizePagedResponse(response.response);
        return {
          items,
          totalCount,
          pageNumber: resolvedPageNumber,
          pageSize: resolvedPageSize,
          append,
          listGeneration,
        };
      }

      return rejectWithValue(response.errorMessage?.message || 'Failed to load shipments');
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to load shipments');
    }
  },
);

export const addShipment = createAsyncThunk(
  'shipment/addShipment',
  async (shipmentPayload, { rejectWithValue }) => {
    try {
      const response = await ShipmentService.createShipment(shipmentPayload);
      if (response.status === 200) {
        return response.response;
      } else {
        return rejectWithValue(response.errorMessage?.message || 'Failed to add shipments');
      }
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to add shipments');
    }
  },
);

export const editShipment = createAsyncThunk(
  'shipment/editShipment',
  async (shipmentPayload, { rejectWithValue }) => {
    try {
      const response = await ShipmentService.createShipment(shipmentPayload);
      if (response.status === 200) {
        return response.response;
      } else {
        return rejectWithValue(response.errorMessage?.message || 'Failed to update shipment');
      }
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to update shipment');
    }
  },
);

export const doOperationOnShipment = createAsyncThunk(
  'shipment/doOperationOnShipment',
  async (shipmentPayload, { rejectWithValue }) => {
    try {
      const response = await ShipmentService.doOperationOnShipment(shipmentPayload);
      if (response.status === 200) {
        return response.response;
      } else {
        return rejectWithValue(response.errorMessage?.message || 'Failed action on shipment');
      }
    } catch (error) {
      return rejectWithValue(error.message || 'Failed action on shipment');
    }
  },
);

export const editShipmentData = createAsyncThunk(
  'shipment/editShipmentData',
  async (id, { rejectWithValue }) => {
    try {
      const response = await ShipmentService.getShipmentById(id);
      if (response.status === 200) {
        return response.response;
      } else {
        return rejectWithValue(response.errorMessage?.message || 'Failed to load shipment data');
      }
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to load shipment data');
    }
  },
);

export const downloadShipments = createAsyncThunk(
  'shipment/downloadShipments',
  async (filters, { rejectWithValue }) => {
    try {
      const response = await ShipmentService.downloadShipment(filters);
      const blob = response;
      saveAs(blob, `shipments.xlsx`);
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to download shipments');
    }
  },
);

export const deleteShipment = createAsyncThunk(
  'shipment/deleteShipment',
  async (shipmentId, { rejectWithValue }) => {
    try {
      const response = await ShipmentService.deleteShipment(shipmentId);
      if (response.status === 200) {
        return shipmentId;
      } else {
        return rejectWithValue(response.errorMessage?.message || 'Failed to delete shipment');
      }
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to delete shipment');
    }
  },
);

const buildShipmentListFilterPayload = (filters, pathname) => {
  const isCustomerDashboard = pathname === '/shipments';

  let customerVal = filters?.customer?.value;
  if (isCustomerDashboard) {
    if (!customerVal || customerVal === '0') {
      const companyId = localStorage.getItem('Company');
      if (companyId) {
        customerVal = companyId;
      }
    }
    if (!customerVal || customerVal === '0') {
      console.warn('Customer dashboard without valid customer ID!');
    }
  }

  // Prepare the filter parameters (POST body for shipment/getallpaged)
  const customerType = localStorage.getItem('CustomerType');
  const headerSearch = String(filters?.searchText ?? '').trim();
  const pageSearch = String(filters?.pageSearchText ?? '').trim();
  const partFromFilter = String(filters?.partNumber ?? '').trim();
  /** Same string is sent for part + PO so the API can filter ShippingItem by either column. */
  const lineItemSearch = partFromFilter || headerSearch;
  const statusVal = filters?.status?.value;
  const filterParams = {
    // Maps to SP @Search — ASP / trailer / PSN / customer name
    searchText: pageSearch,
    partNumber: lineItemSearch,
    poNumber: lineItemSearch,
    shipmentDate: filters?.shipDate,
    customerType: customerType !== null ? customerType : '',
  };

  // Only send status when a real status is selected (skip placeholder / empty)
  if (statusVal && String(statusVal).trim() !== '' && statusVal !== 'Select Status') {
    filterParams.status = statusVal;
  }

  // Customer dashboard still filters by customer id.
  // Warehouse customer dropdown uses searchText (same as top search bar).
  if (isCustomerDashboard && customerVal && customerVal !== '0') {
    filterParams.customer = customerVal;
  }

  return filterParams;
};

export const fetchShipmentsWithFilters = (filterOverrides = {}) => (dispatch, getState) => {
  const filters = {
    ...getState().shipment.filters,
    ...filterOverrides,
  };
  const pathname = window.location.pathname;
  const filterParams = buildShipmentListFilterPayload(filters, pathname);
  dispatch(
    fetchShipments({
      ...filterParams,
      pageNumber: 1,
      pageSize: getState().shipment.pagination.pageSize || DEFAULT_SHIPMENT_PAGE_SIZE,
      append: false,
    }),
  );
};

export const loadMoreShipments = () => (dispatch, getState) => {
  const state = getState().shipment;
  if (state.loading || state.loadingMore || !state.pagination.hasMore) {
    return;
  }

  const pathname = window.location.pathname;
  const filterParams = buildShipmentListFilterPayload(state.filters, pathname);
  dispatch(
    fetchShipments({
      ...filterParams,
      pageNumber: state.pagination.pageNumber + 1,
      pageSize: state.pagination.pageSize || DEFAULT_SHIPMENT_PAGE_SIZE,
      append: true,
    }),
  );
};

export const downloadShipmentsWithFilters = () => (dispatch, getState) => {
  const filters = getState().shipment.filters;
  const pathname = window.location.pathname;
  dispatch(downloadShipments(buildShipmentListFilterPayload(filters, pathname)));
};

export const { reducer, actions } = createSlice({
  name: 'shipment',
  initialState: INITIAL_STATE,
  reducers: {
    setFilters: (state, action) => {
      state.filters = action.payload;
    },
    resetFilters: state => {
      state.filters = INITIAL_STATE.filters;
    },
    setFilterVisible: (state, action) => {
      state.filterVisible = action.payload;
    },
    setSearchText: (state, action) => {
      state.filters.searchText = action.payload;
    },
    setPageSearchText: (state, action) => {
      state.filters.pageSearchText = action.payload;
    },
    setCustomer: (state, action) => {
      state.filters.customer = action.payload;
    },
    setStatus: (state, action) => {
      state.filters.status = action.payload;
    },
    setShipDate: (state, action) => {
      state.filters.shipDate = action.payload;
    },
    setPartNumber: (state, action) => {
      state.filters.partNumber = action.payload;
    },
    setTileView: (state, action) => {
      state.tileView = action.payload; // Add reducer to update tileView
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchShipments.pending, (state, action) => {
        const append = Boolean(action.meta.arg?.append);
        state.error = null;
        if (append) {
          state.loadingMore = true;
          state.shipmentLoadMoreRequestId = action.meta.requestId;
        } else {
          state.loading = true;
          state.loadingMore = false;
          state.listGeneration += 1;
          state.shipmentListRequestId = action.meta.requestId;
        }
      })
      .addCase(fetchShipments.fulfilled, (state, action) => {
        const { items, totalCount, pageNumber, pageSize, append, listGeneration } = action.payload;

        if (append) {
          if (action.meta.requestId !== state.shipmentLoadMoreRequestId) {
            return;
          }
          if (listGeneration !== state.listGeneration) {
            state.loadingMore = false;
            return;
          }

          const existingIds = new Set(state.data.map(s => s.id));
          const newItems = items.filter(s => !existingIds.has(s.id));
          state.data = [...state.data, ...newItems];
          state.loadingMore = false;
        } else {
          if (action.meta.requestId !== state.shipmentListRequestId) {
            return;
          }
          state.data = items;
          state.loading = false;
          state.loadingMore = false;
        }

        state.pagination = {
          pageNumber,
          pageSize,
          totalCount,
          hasMore: state.data.length < totalCount,
        };
      })
      .addCase(fetchShipments.rejected, (state, action) => {
        const append = Boolean(action.meta.arg?.append);
        if (append) {
          if (action.meta.requestId !== state.shipmentLoadMoreRequestId) {
            return;
          }
          state.loadingMore = false;
        } else {
          if (action.meta.requestId !== state.shipmentListRequestId) {
            return;
          }
          state.loading = false;
        }
        state.error = action.payload;
      })
      .addCase(addShipment.fulfilled, (state, action) => {
        const newShipment = {
          ...action.payload,
          isNewlyCreated: true,
        };
        state.data = [newShipment, ...state.data];
        state.pagination.totalCount += 1;
      })
      .addCase(addShipment.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(editShipment.fulfilled, (state, action) => {
        const saved = action.payload || {};
        const submitted = action.meta.arg || {};
        const index = state.data.findIndex(
          s => String(s.id) === String(saved.id ?? submitted.id),
        );
        if (index !== -1) {
          const existing = state.data[index];
          const customerChanged =
            submitted.customerId !== undefined &&
            submitted.customerId !== null &&
            String(submitted.customerId) !== String(existing.customerId ?? '');
          state.data[index] = {
            ...existing,
            ...saved,
            // Match list tiles: name stays in customerName (bottom). Do not put the
            // selected short name in customerShortName, or the tile order changes until refresh.
            customerShortName: customerChanged ? null : existing.customerShortName,
            customerName: customerChanged
              ? submitted.customerShortName || null
              : existing.customerName ?? null,
          };
        }
      })
      .addCase(editShipment.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(deleteShipment.fulfilled, (state, action) => {
        const newData = state.data.filter(s => s.id !== action.payload);
        state.data = newData;
        state.pagination.totalCount = Math.max(0, state.pagination.totalCount - 1);
        state.pagination.hasMore = state.data.length < state.pagination.totalCount;
      })
      .addCase(editShipmentData.pending, state => {
        state.shipmentLoading = true;
      })
      .addCase(editShipmentData.fulfilled, state => {
        state.shipmentLoading = false;
      })
      .addCase(editShipmentData.rejected, state => {
        state.shipmentLoading = false;
      });
  },
});

redux.listener.startListening({
  matcher: isAnyOf(addShipment.rejected, editShipment.rejected, editShipmentData.rejected),
  effect: (action, listenerApi) => {
    listenerApi.dispatch(
      showToast({
        message: action.payload,
        type: 'danger',
      }),
    );
  },
});

redux.listener.startListening({
  matcher: isAnyOf(addShipment.fulfilled, editShipment.fulfilled),
  effect: (action, listenerApi) => {
    listenerApi.dispatch(closeModal(SHIPMENT_ADD_EDIT));
    listenerApi.dispatch(
      showToast({
        message: 'Shipment saved successfully',
        type: 'success',
      }),
    );
  },
});

redux.listener.startListening({
  matcher: isAnyOf(doOperationOnShipment.fulfilled),
  effect: (action, listenerApi) => {
    listenerApi.dispatch(
      showToast({
        message: 'Shipment operation successful',
        type: 'success',
      }),
    );
  },
});

redux.listener.startListening({
  matcher: isAnyOf(editShipmentData.fulfilled),
  effect: (action, listenerApi) => {
    listenerApi.dispatch(showModal(SHIPMENT_ADD_EDIT, action.payload));
  },
});

redux.listener.startListening({
  matcher: isAnyOf(deleteShipment.fulfilled),
  effect: (action, listenerApi) => {
    listenerApi.dispatch(
      showToast({
        message: 'Shipment deleted successfully',
        type: 'success',
      }),
    );
  },
});

redux.injectReducer(STORE_KEY, reducer);
